import React, { useState, useEffect } from 'react';
import jsPDF from 'jspdf';

const StudentRegistration = ({ onNavigate }) => {
  const [isMobile, setIsMobile] = useState(false);
  const [step, setStep] = useState(0); // 0 = phone verification, 1-4 = form steps (hostel removed)
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [messageType, setMessageType] = useState(''); // 'error', 'info', 'warning'
  const [success, setSuccess] = useState(false);
  const [applicationNumber, setApplicationNumber] = useState('');

  // OTP verification state
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [isRestored, setIsRestored] = useState(false); // Track if draft was restored

  // Form data - HOSTEL FIELDS REMOVED
  const [formData, setFormData] = useState({
    phoneNumber: '',
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
    dateOfBirth: '',
    gender: '',
    bloodGroup: '',

    studentId: '',
    course: '',
    batchName: '',
    department: '', // Now will be dropdown

    guardianName: '',
    guardianPhone: '',
    guardianEmail: '',
    guardianRelation: '',

    permanentAddress: '',
    city: '',
    state: '',
    pincode: '',

    emergencyContactName: '',
    emergencyContactPhone: '',
    emergencyContactRelation: '',

    medicalConditions: '',
    allergies: '',
    specialRequirements: ''
  });

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://app.learninghub.ind.in/api';

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // PDF Generation Function
  const generatePDF = async (appNumber, data) => {
    const doc = new jsPDF();

    // Header background
    doc.setFillColor(99, 102, 241);
    doc.rect(0, 0, 210, 50, 'F');

    // Try to add logo
    try {
      const logo = await new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('Logo not found'));
        img.src = '/srims_logo.png';
        // Add a timeout to prevent hanging
        setTimeout(() => reject(new Error('Logo load timeout')), 2000);
      });

      // Add logo on top of the blue header (left side)
      doc.addImage(logo, 'PNG', 15, 8, 35, 35);
    } catch (error) {
      console.log('Logo not loaded:', error.message);
    }

    // Header text with institution name
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('STUDENT REGISTRATION', 105, 20, { align: 'center' });

    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.text('Shri Ramkrishna Institute of Medical Sciences', 105, 32, { align: 'center' });
    doc.text('& Sanaka Hospitals', 105, 40, { align: 'center' });

    // Application ID Box
    doc.setFillColor(240, 240, 255);
    doc.rect(15, 55, 180, 15, 'F');
    doc.setTextColor(99, 102, 241);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(`Application ID: ${appNumber}`, 105, 64, { align: 'center' });

    // Helper function to draw tables
    const drawTable = (startY, title, data, columnWidths = [60, 120]) => {
      doc.setTextColor(0, 0, 0);

      // Section Title
      doc.setFillColor(99, 102, 241);
      doc.rect(15, startY, 180, 10, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text(title, 20, startY + 7);

      let yPos = startY + 10;
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(10);

      // Draw table rows
      data.forEach(([label, value], index) => {
        // Alternate row colors
        if (index % 2 === 0) {
          doc.setFillColor(250, 250, 250);
        } else {
          doc.setFillColor(255, 255, 255);
        }
        doc.rect(15, yPos, 180, 8, 'F');

        // Draw borders
        doc.setDrawColor(200, 200, 200);
        doc.rect(15, yPos, columnWidths[0], 8, 'S');
        doc.rect(15 + columnWidths[0], yPos, columnWidths[1], 8, 'S');

        // Draw text
        doc.setFont('helvetica', 'bold');
        doc.text(label, 18, yPos + 5.5);
        doc.setFont('helvetica', 'normal');
        doc.text(String(value), 18 + columnWidths[0], yPos + 5.5);

        yPos += 8;
      });

      return yPos + 5; // Return next Y position
    };

    // Personal Information
    const personalInfo = [
      ['Full Name:', data.fullName],
      ['Email:', data.email],
      ['Phone:', data.phoneNumber],
      ['Date of Birth:', data.dateOfBirth || 'Not provided'],
      ['Gender:', data.gender || 'Not provided'],
      ['Blood Group:', data.bloodGroup || 'Not provided']
    ];

    let currentY = 80;
    currentY = drawTable(currentY, 'Personal Information', personalInfo);

    // Academic Information
    const academicInfo = [
      ['Student ID:', data.studentId || 'Not provided'],
      ['Course:', data.course || 'Not provided'],
      ['Batch Name:', data.batchName || 'Not provided'],
      ['Department:', data.department || 'Not provided'],
    ];

    currentY = drawTable(currentY, 'Academic Information', academicInfo);

    // Guardian & Emergency Contact Information
    const guardianInfo = [
      ['Guardian Name:', data.guardianName || 'Not provided'],
      ['Guardian Phone:', data.guardianPhone || 'Not provided'],
      ['Guardian Email:', data.guardianEmail || 'Not provided'],
      ['Relation:', data.guardianRelation || 'Not provided'],
      ['Emergency Contact:', data.emergencyContactName || 'Not provided'],
      ['Emergency Phone:', data.emergencyContactPhone || 'Not provided'],
      ['Emergency Relation:', data.emergencyContactRelation || 'Not provided']
    ];

    // Check if we need a new page
    if (currentY > 220) {
      doc.addPage();
      currentY = 20;
    }

    currentY = drawTable(currentY, 'Guardian & Emergency Contact', guardianInfo);

    // Address Information
    const addressInfo = [
      ['Permanent Address:', data.permanentAddress || 'Not provided'],
      ['City:', data.city || 'Not provided'],
      ['State:', data.state || 'Not provided'],
      ['PIN Code:', data.pincode || 'Not provided']
    ];

    // Check if we need a new page
    if (currentY > 220) {
      doc.addPage();
      currentY = 20;
    }

    currentY = drawTable(currentY, 'Address Information', addressInfo);

    // Additional Information
    const additionalInfo = [];
    if (data.medicalConditions) {
      additionalInfo.push(['Medical Conditions:', data.medicalConditions]);
    }
    if (data.allergies) {
      additionalInfo.push(['Allergies:', data.allergies]);
    }
    if (data.specialRequirements) {
      additionalInfo.push(['Special Requirements:', data.specialRequirements]);
    }

    if (additionalInfo.length > 0) {
      // Check if we need a new page
      if (currentY > 220) {
        doc.addPage();
        currentY = 20;
      }

      currentY = drawTable(currentY, 'Additional Information', additionalInfo);
    }

    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(9);
      doc.setTextColor(128, 128, 128);
      doc.text(
        `Application Date: ${new Date().toLocaleDateString('en-IN')} | Page ${i} of ${pageCount}`,
        105,
        285,
        { align: 'center' }
      );
    }

    // Save PDF
    doc.save(`Student_Registration_${appNumber}.pdf`);
  };

  const getSuffix = (num) => {
    const j = num % 10;
    const k = num % 100;
    if (j === 1 && k !== 11) return 'st';
    if (j === 2 && k !== 12) return 'nd';
    if (j === 3 && k !== 13) return 'rd';
    return 'th';
  };

  const handleSendOTP = async () => {
    if (!phoneNumber || phoneNumber.length !== 10) {
      setError('Please enter a valid 10-digit phone number');
      setMessageType('error');
      return;
    }

    setLoading(true);
    setError('');
    setMessageType('');

    try {
      const response = await fetch(`${API_BASE_URL}/registration/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phoneNumber })
      });

      const data = await response.json();

      if (data.isRegistered) {
        setError(data.message);
        setMessageType('info');
        setLoading(false);
        return;
      }

      if (data.hasPendingApplication) {
        setError(data.message);
        setMessageType('warning');
        setLoading(false);
        return;
      }

      if (response.ok && data.success) {
        setOtpSent(true);
        setMessageType('');
      } else {
        setError(data.message || 'Failed to send OTP');
        setMessageType('error');
      }
    } catch (err) {
      console.error('Send OTP Error:', err);
      setError('Network error. Please check your connection.');
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  // Save the current state as a draft (Auto-save)
  const saveDraft = async (currentStep) => {
    try {
      // Don't save if no phone number
      if (!phoneNumber) return;

      await fetch(`${API_BASE_URL}/registration/save-draft`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phoneNumber,
          currentStep,
          ...formData
        })
      });
      // Silent save - no UI feedback needed unless error
    } catch (err) {
      console.error('Auto-save error:', err);
    }
  };

  const handleVerifyOTP = async () => {
    if (!otp || otp.length !== 6) {
      setError('Please enter a valid 6-digit OTP');
      setMessageType('error');
      return;
    }

    setLoading(true);
    setError('');
    setMessageType('');

    try {
      const response = await fetch(`${API_BASE_URL}/registration/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phoneNumber, otp })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setPhoneVerified(true);
        setFormData(prev => ({ ...prev, phoneNumber }));

        // Handle Draft Restoration
        if (data.draftData) {
          setFormData(prev => ({
            ...prev,
            ...data.draftData,
            phoneNumber // Ensure phone number stays
          }));
          setStep(data.currentStep || 1);
          setIsRestored(true);
          setMessageType('info');
          setError('📝 Your previous progress has been restored!');

          // Clear "restored" message after 5 seconds
          setTimeout(() => {
            setIsRestored(false);
            setMessageType('');
            setError('');
          }, 5000);
        } else {
          setStep(1);
          setError('');
          setMessageType('');
        }
      } else {
        setError(data.message || 'Invalid OTP. Please try again.');
        setMessageType('error');
      }
    } catch (err) {
      console.error('Verify OTP Error:', err);
      setError('Network error. Please try again.');
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
    setMessageType('');
  };

  const validatePassword = () => {
    const { password, confirmPassword } = formData;

    if (!password || password.length < 8) {
      setError('Password must be at least 8 characters long');
      setMessageType('error');
      return false;
    }

    if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(password)) {
      setError('Password must contain uppercase, lowercase, and number');
      setMessageType('error');
      return false;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setMessageType('error');
      return false;
    }

    return true;
  };

  const validateStep = (currentStep) => {
    switch (currentStep) {
      case 1:
        if (!formData.fullName || !formData.email) {
          setError('Please fill all required fields');
          setMessageType('error');
          return false;
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
          setError('Please enter a valid email address');
          setMessageType('error');
          return false;
        }
        if (!validatePassword()) {
          return false;
        }
        break;

      case 2:
        if (!formData.course || !formData.department) {
          setError('Please fill all required fields');
          setMessageType('error');
          return false;
        }
        break;

      case 3:
        if (!formData.guardianName || !formData.guardianPhone) {
          setError('Please fill all required guardian fields');
          setMessageType('error');
          return false;
        }
        if (formData.guardianPhone.length !== 10) {
          setError('Please enter valid 10-digit phone numbers');
          setMessageType('error');
          return false;
        }
        break;
    }
    return true;
  };

  const handleNext = () => {
    if (validateStep(step)) {
      const nextStep = step + 1;
      setStep(nextStep);
      saveDraft(nextStep); // Auto-save on next
      setError('');
      setMessageType('');
      window.scrollTo(0, 0);
    }
  };

  const handleBack = () => {
    const prevStep = step - 1;
    setStep(prevStep);
    saveDraft(prevStep); // Auto-save on back
    setError('');
    setMessageType('');
    window.scrollTo(0, 0);
  };

  const handleSubmit = async () => {
    if (!validateStep(step)) return;

    setLoading(true);
    setError('');
    setMessageType('');

    try {
      const response = await fetch(`${API_BASE_URL}/registration/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setApplicationNumber(data.applicationNumber);
        setSuccess(true);

        // AUTO GENERATE AND DOWNLOAD PDF
        setTimeout(async () => {
          await generatePDF(data.applicationNumber, formData);
        }, 500);

      } else {
        setError(data.message || 'Registration failed. Please try again.');
        setMessageType('error');
      }
    } catch (err) {
      console.error('Submit Error:', err);
      setError('Network error. Please try again.');
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  const passwordStrength = React.useMemo(() => {
    const { password } = formData;
    if (!password) return { strength: 0, label: '', color: '#e5e7eb' };

    let strength = 0;
    if (password.length >= 8) strength += 25;
    if (password.length >= 12) strength += 15;
    if (/[a-z]/.test(password)) strength += 15;
    if (/[A-Z]/.test(password)) strength += 15;
    if (/\d/.test(password)) strength += 15;
    if (/[^a-zA-Z\d]/.test(password)) strength += 15;

    if (strength < 40) return { strength, label: 'Weak', color: '#ef4444' };
    if (strength < 70) return { strength, label: 'Medium', color: '#f59e0b' };
    return { strength, label: 'Strong', color: '#10b981' };
  }, [formData.password]);

  const renderPhoneVerification = () => (
    <>
      <div style={styles.phoneVerificationContainer}>
        <div style={styles.phoneIcon}>📱</div>
        <h2 style={styles.formTitle}>Verify Your Phone Number</h2>
        <p style={styles.formSubtitle}>
          {!otpSent
            ? 'We\'ll send you a verification code via WhatsApp'
            : 'Enter the 6-digit code sent to your WhatsApp'}
        </p>
      </div>

      {error && (
        <div style={{
          ...styles.error,
          ...(messageType === 'info' && styles.infoMessage),
          ...(messageType === 'warning' && styles.warningMessage)
        }}>
          {messageType === 'error' && '❌ '}
          {messageType === 'info' && 'ℹ️ '}
          {messageType === 'warning' && '⚠️ '}
          {error}
        </div>
      )}

      {!otpSent ? (
        <>
          <div style={styles.phoneInputGroup}>
            <span style={styles.countryCode}>+91</span>
            <input
              type="tel"
              value={phoneNumber}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '');
                if (value.length <= 10) {
                  setPhoneNumber(value);
                  setError('');
                  setMessageType('');
                }
              }}
              style={styles.phoneInput}
              placeholder="Phone Number"
              maxLength="10"
              onFocus={(e) => e.target.style.border = '2px solid #6366f1'}
              onBlur={(e) => e.target.style.border = '2px solid #e5e7eb'}
            />
          </div>

          <div style={styles.buttonContainer}>
            <button
              onClick={handleSendOTP}
              disabled={loading}
              style={{
                ...styles.button,
                ...styles.buttonPrimary,
                width: '100%',
                justifyContent: 'center',
                opacity: loading ? 0.7 : 1
              }}
              onMouseEnter={(e) => !loading && (e.target.style.transform = 'translateY(-2px)')}
              onMouseLeave={(e) => !loading && (e.target.style.transform = 'translateY(0)')}
            >
              {loading ? '⏳ Sending...' : '📱 Send OTP via WhatsApp'}
            </button>
          </div>
        </>
      ) : (
        <>
          <input
            type="text"
            value={otp}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '');
              if (value.length <= 6) {
                setOtp(value);
                setError('');
                setMessageType('');
              }
            }}
            style={styles.otpInput}
            placeholder="000000"
            maxLength="6"
            autoFocus
            onFocus={(e) => e.target.style.border = '2px solid #6366f1'}
            onBlur={(e) => e.target.style.border = '2px solid #e5e7eb'}
          />

          <div style={styles.buttonContainer}>
            <button
              onClick={() => {
                setOtpSent(false);
                setOtp('');
                setError('');
                setMessageType('');
              }}
              style={{ ...styles.button, ...styles.buttonSecondary }}
            >
              ← Change Number
            </button>
            <button
              onClick={handleVerifyOTP}
              disabled={loading}
              style={{
                ...styles.button,
                ...styles.buttonPrimary,
                opacity: loading ? 0.7 : 1
              }}
              onMouseEnter={(e) => !loading && (e.target.style.transform = 'translateY(-2px)')}
              onMouseLeave={(e) => !loading && (e.target.style.transform = 'translateY(0)')}
            >
              {loading ? '⏳ Verifying...' : '✓ Verify OTP'}
            </button>
          </div>

          <p style={{ ...styles.formSubtitle, textAlign: 'center', marginTop: '16px', marginBottom: 0 }}>
            Didn't receive code?{' '}
            <span
              style={{
                color: '#6366f1',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontWeight: '600',
                textDecoration: 'underline'
              }}
              onClick={() => !loading && handleSendOTP()}
            >
              Resend OTP
            </span>
          </p>
        </>
      )}
    </>
  );

  const renderStep1 = () => (
    <>
      <h2 style={styles.formTitle}>Personal Information</h2>
      <p style={styles.formSubtitle}>Let's start with your basic details and create your account</p>

      <div style={styles.formGrid}>
        <div style={{ ...styles.formGroup, ...styles.formGroupFull }}>
          <label style={styles.label}>
            Full Name <span style={styles.required}>*</span>
          </label>
          <input
            type="text"
            name="fullName"
            value={formData.fullName}
            onChange={handleInputChange}
            style={styles.input}
            placeholder="Enter your full name"
            onFocus={(e) => e.target.style.border = '2px solid #6366f1'}
            onBlur={(e) => e.target.style.border = '2px solid #e5e7eb'}
          />
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>
            Email Address <span style={styles.required}>*</span>
          </label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            style={styles.input}
            placeholder="your.email@example.com"
            onFocus={(e) => e.target.style.border = '2px solid #6366f1'}
            onBlur={(e) => e.target.style.border = '2px solid #e5e7eb'}
          />
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Phone Number (Verified) ✓</label>
          <input
            type="tel"
            value={formData.phoneNumber}
            style={{ ...styles.input, backgroundColor: '#e5e7eb', cursor: 'not-allowed' }}
            disabled
          />
        </div>

        {/* PASSWORD FIELDS */}
        <div style={styles.formGroup}>
          <label style={styles.label}>
            Create Password <span style={styles.required}>*</span>
          </label>
          <input
            type="password"
            name="password"
            value={formData.password}
            onChange={handleInputChange}
            style={styles.input}
            placeholder="Create a strong password"
            onFocus={(e) => e.target.style.border = '2px solid #6366f1'}
            onBlur={(e) => e.target.style.border = '2px solid #e5e7eb'}
          />
          <div style={styles.passwordHint}>
            • At least 8 characters<br />
            • Include uppercase, lowercase & numbers
          </div>
          {formData.password && (
            <>
              <div style={{
                ...styles.passwordStrength,
                width: `${passwordStrength.strength}%`,
                backgroundColor: passwordStrength.color
              }} />
              <div style={{
                fontSize: '12px',
                color: passwordStrength.color,
                marginTop: '4px',
                fontWeight: '600'
              }}>
                {passwordStrength.label}
              </div>
            </>
          )}
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>
            Confirm Password <span style={styles.required}>*</span>
          </label>
          <input
            type="password"
            name="confirmPassword"
            value={formData.confirmPassword}
            onChange={handleInputChange}
            style={styles.input}
            placeholder="Re-enter your password"
            onFocus={(e) => e.target.style.border = '2px solid #6366f1'}
            onBlur={(e) => e.target.style.border = '2px solid #e5e7eb'}
          />
          {formData.confirmPassword && (
            <div style={{
              fontSize: '12px',
              marginTop: '6px',
              color: formData.password === formData.confirmPassword ? '#10b981' : '#ef4444',
              fontWeight: '600'
            }}>
              {formData.password === formData.confirmPassword ? '✓ Passwords match' : '✗ Passwords do not match'}
            </div>
          )}
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Date of Birth</label>
          <input
            type="date"
            name="dateOfBirth"
            value={formData.dateOfBirth}
            onChange={handleInputChange}
            style={styles.input}
            onFocus={(e) => e.target.style.border = '2px solid #6366f1'}
            onBlur={(e) => e.target.style.border = '2px solid #e5e7eb'}
          />
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Gender</label>
          <select
            name="gender"
            value={formData.gender}
            onChange={handleInputChange}
            style={styles.select}
            onFocus={(e) => e.target.style.border = '2px solid #6366f1'}
            onBlur={(e) => e.target.style.border = '2px solid #e5e7eb'}
          >
            <option value="">Select Gender</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="other">Other</option>
          </select>
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Blood Group</label>
          <select
            name="bloodGroup"
            value={formData.bloodGroup}
            onChange={handleInputChange}
            style={styles.select}
            onFocus={(e) => e.target.style.border = '2px solid #6366f1'}
            onBlur={(e) => e.target.style.border = '2px solid #e5e7eb'}
          >
            <option value="">Select Blood Group</option>
            <option value="A+">A+</option>
            <option value="A-">A-</option>
            <option value="B+">B+</option>
            <option value="B-">B-</option>
            <option value="AB+">AB+</option>
            <option value="AB-">AB-</option>
            <option value="O+">O+</option>
            <option value="O-">O-</option>
          </select>
        </div>
      </div>
    </>
  );

  const renderStep2 = () => (
    <>
      <h2 style={styles.formTitle}>Academic Information</h2>
      <p style={styles.formSubtitle}>Tell us about your academic background</p>

      <div style={styles.formGrid}>
        <div style={styles.formGroup}>
          <label style={styles.label}>Student ID (if available)</label>
          <input
            type="text"
            name="studentId"
            value={formData.studentId}
            onChange={handleInputChange}
            style={styles.input}
            placeholder="Enter your student ID"
            onFocus={(e) => e.target.style.border = '2px solid #6366f1'}
            onBlur={(e) => e.target.style.border = '2px solid #e5e7eb'}
          />
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>
            Course <span style={styles.required}>*</span>
          </label>
          <select
            name="course"
            value={formData.course}
            onChange={handleInputChange}
            style={styles.select}
            onFocus={(e) => e.target.style.border = '2px solid #6366f1'}
            onBlur={(e) => e.target.style.border = '2px solid #e5e7eb'}
          >
            <option value="">Select Course</option>
            <option value="BSc">BSc</option>
            <option value="MBBS">MBBS</option>
            <option value="GNM">GNM</option>
            <option value="MD">MD</option>
            <option value="MS">MS</option>
            <option value="BDS">BDS</option>
          </select>
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Batch</label>
          <input
            type="text"
            name="batchName"
            value={formData.batchName}
            onChange={handleInputChange}
            style={styles.input}
            onFocus={(e) => e.target.style.border = '2px solid #6366f1'}
            onBlur={(e) => e.target.style.border = '2px solid #e5e7eb'}
          />
        </div>
        <div style={styles.formGroup}>

          <label style={styles.label}>
            Department <span style={styles.required}>*</span>
          </label>
          <select
            name="department"
            value={formData.department}
            onChange={handleInputChange}
            style={styles.select}
            onFocus={(e) => e.target.style.border = '2px solid #6366f1'}
            onBlur={(e) => e.target.style.border = '2px solid #e5e7eb'}
          >
            <option value="">Select Department</option>
            <option value="Anaesthesiology">Anaesthesiology</option>
            <option value="General Medicine">General Medicine</option>
            <option value="General Surgery">General Surgery</option>
            <option value="MBBS">MBBS</option>
            <option value="Nursing">Nursing</option>
            <option value="Obstetrics & Gynaecology">Obstetrics & Gynaecology</option>
            <option value="Orthopaedics">Orthopaedics</option>
            <option value="Otothinolaryngology(ENT)">Otothinolaryngology(ENT)</option>
            <option value="Paediatrics">Paediatrics</option>
            <option value="Psychiatry">Psychiatry</option>
            <option value="Radiodiagnosis">Radiodiagnosis</option>
            <option value="BDS">BDS</option>
          </select>
        </div>
      </div>
    </>
  );

  const renderStep3 = () => (
    <>
      <h2 style={styles.formTitle}>Guardian & Emergency Contact</h2>
      <p style={styles.formSubtitle}>Provide contact information for emergencies</p>

      <div style={styles.formGrid}>
        <div style={{ ...styles.formGroup, ...styles.formGroupFull }}>
          <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1f2937', marginBottom: '16px' }}>
            👨‍👩‍👦 Guardian Information
          </h3>
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>
            Guardian Name <span style={styles.required}>*</span>
          </label>
          <input
            type="text"
            name="guardianName"
            value={formData.guardianName}
            onChange={handleInputChange}
            style={styles.input}
            placeholder="Guardian's full name"
            onFocus={(e) => e.target.style.border = '2px solid #6366f1'}
            onBlur={(e) => e.target.style.border = '2px solid #e5e7eb'}
          />
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>
            Guardian Phone <span style={styles.required}>*</span>
          </label>
          <input
            type="tel"
            name="guardianPhone"
            value={formData.guardianPhone}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '');
              if (value.length <= 10) {
                handleInputChange({ target: { name: 'guardianPhone', value } });
              }
            }}
            style={styles.input}
            placeholder="10-digit phone number"
            maxLength="10"
            onFocus={(e) => e.target.style.border = '2px solid #6366f1'}
            onBlur={(e) => e.target.style.border = '2px solid #e5e7eb'}
          />
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Guardian Email</label>
          <input
            type="email"
            name="guardianEmail"
            value={formData.guardianEmail}
            onChange={handleInputChange}
            style={styles.input}
            placeholder="guardian@example.com"
            onFocus={(e) => e.target.style.border = '2px solid #6366f1'}
            onBlur={(e) => e.target.style.border = '2px solid #e5e7eb'}
          />
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Relation</label>
          <select
            name="guardianRelation"
            value={formData.guardianRelation}
            onChange={handleInputChange}
            style={styles.select}
            onFocus={(e) => e.target.style.border = '2px solid #6366f1'}
            onBlur={(e) => e.target.style.border = '2px solid #e5e7eb'}
          >
            <option value="">Select Relation</option>
            <option value="father">Father</option>
            <option value="mother">Mother</option>
            <option value="brother">Brother</option>
            <option value="sister">Sister</option>
            <option value="uncle">Uncle</option>
            <option value="aunt">Aunt</option>
            <option value="other">Other</option>
          </select>
        </div>

        <div style={{ ...styles.formGroup, ...styles.formGroupFull }}>
          <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1f2937', marginTop: '24px', marginBottom: '16px' }}>
            🚨 Emergency Contact
          </h3>
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Emergency Contact Name</label>
          <input
            type="text"
            name="emergencyContactName"
            value={formData.emergencyContactName}
            onChange={handleInputChange}
            style={styles.input}
            placeholder="Full name"
            onFocus={(e) => e.target.style.border = '2px solid #6366f1'}
            onBlur={(e) => e.target.style.border = '2px solid #e5e7eb'}
          />
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Emergency Phone</label>
          <input
            type="tel"
            name="emergencyContactPhone"
            value={formData.emergencyContactPhone}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '');
              if (value.length <= 10) {
                handleInputChange({ target: { name: 'emergencyContactPhone', value } });
              }
            }}
            style={styles.input}
            placeholder="10-digit phone number"
            maxLength="10"
            onFocus={(e) => e.target.style.border = '2px solid #6366f1'}
            onBlur={(e) => e.target.style.border = '2px solid #e5e7eb'}
          />
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Relation</label>
          <select
            name="emergencyContactRelation"
            value={formData.emergencyContactRelation}
            onChange={handleInputChange}
            style={styles.select}
            onFocus={(e) => e.target.style.border = '2px solid #6366f1'}
            onBlur={(e) => e.target.style.border = '2px solid #e5e7eb'}
          >
            <option value="">Select Relation</option>
            <option value="father">Father</option>
            <option value="mother">Mother</option>
            <option value="brother">Brother</option>
            <option value="sister">Sister</option>
            <option value="friend">Friend</option>
            <option value="relative">Relative</option>
            <option value="other">Other</option>
          </select>
        </div>
      </div>
    </>
  );

  const renderStep4 = () => (
    <>
      <h2 style={styles.formTitle}>Additional Information</h2>
      <p style={styles.formSubtitle}>Help us serve you better with these optional details</p>

      <div style={styles.formGrid}>
        <div style={{ ...styles.formGroup, ...styles.formGroupFull }}>
          <label style={styles.label}>Permanent Address</label>
          <textarea
            name="permanentAddress"
            value={formData.permanentAddress}
            onChange={handleInputChange}
            style={styles.textarea}
            placeholder="Full permanent address"
            onFocus={(e) => e.target.style.border = '2px solid #6366f1'}
            onBlur={(e) => e.target.style.border = '2px solid #e5e7eb'}
          />
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>City</label>
          <input
            type="text"
            name="city"
            value={formData.city}
            onChange={handleInputChange}
            style={styles.input}
            placeholder="City"
            onFocus={(e) => e.target.style.border = '2px solid #6366f1'}
            onBlur={(e) => e.target.style.border = '2px solid #e5e7eb'}
          />
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>State</label>
          <input
            type="text"
            name="state"
            value={formData.state}
            onChange={handleInputChange}
            style={styles.input}
            placeholder="State"
            onFocus={(e) => e.target.style.border = '2px solid #6366f1'}
            onBlur={(e) => e.target.style.border = '2px solid #e5e7eb'}
          />
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>PIN Code</label>
          <input
            type="text"
            name="pincode"
            value={formData.pincode}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '');
              if (value.length <= 6) {
                handleInputChange({ target: { name: 'pincode', value } });
              }
            }}
            style={styles.input}
            placeholder="6-digit PIN code"
            maxLength="6"
            onFocus={(e) => e.target.style.border = '2px solid #6366f1'}
            onBlur={(e) => e.target.style.border = '2px solid #e5e7eb'}
          />
        </div>

        <div style={{ ...styles.formGroup, ...styles.formGroupFull }}>
          <label style={styles.label}>Medical Conditions (if any)</label>
          <textarea
            name="medicalConditions"
            value={formData.medicalConditions}
            onChange={handleInputChange}
            style={styles.textarea}
            placeholder="Please mention any medical conditions we should be aware of"
            onFocus={(e) => e.target.style.border = '2px solid #6366f1'}
            onBlur={(e) => e.target.style.border = '2px solid #e5e7eb'}
          />
        </div>

        <div style={{ ...styles.formGroup, ...styles.formGroupFull }}>
          <label style={styles.label}>Allergies</label>
          <input
            type="text"
            name="allergies"
            value={formData.allergies}
            onChange={handleInputChange}
            style={styles.input}
            placeholder="Food allergies, medicine allergies, etc."
            onFocus={(e) => e.target.style.border = '2px solid #6366f1'}
            onBlur={(e) => e.target.style.border = '2px solid #e5e7eb'}
          />
        </div>

        <div style={{ ...styles.formGroup, ...styles.formGroupFull }}>
          <label style={styles.label}>Special Requirements</label>
          <textarea
            name="specialRequirements"
            value={formData.specialRequirements}
            onChange={handleInputChange}
            style={styles.textarea}
            placeholder="Any special requirements or accommodations needed"
            onFocus={(e) => e.target.style.border = '2px solid #6366f1'}
            onBlur={(e) => e.target.style.border = '2px solid #e5e7eb'}
          />
        </div>
      </div>
    </>
  );

  if (success) {
    return (
      <div style={styles.container}>
        <div style={styles.formContainer}>
          <div style={styles.successContainer}>
            <div style={styles.successIcon}>✔</div>
            <h2 style={styles.successTitle}>Registration Successful!</h2>

            <div style={styles.appNumberBox}>
              <div style={styles.appNumberLabel}>Your Application Number</div>
              <div style={styles.appNumberValue}>{applicationNumber}</div>
            </div>

            <p style={styles.successMessage}>
              Thank you for registering! Your application has been submitted successfully.
              <br /><br />
              <strong>📄 Your registration PDF has been downloaded automatically!</strong>
              <br /><br />
              <strong>Please save your application number: {applicationNumber}</strong>
              <br /><br />
              You will receive a confirmation email shortly. Our team will review your application
              and contact you within 24-48 hours.
              <br /><br />
              <strong style={{ color: '#6366f1', fontSize: '18px' }}>
                🔐 Once approved, you can login using:<br />
                Phone: {formData.phoneNumber}<br />
                Password: (The password you created)
              </strong>
              <br /><br />
              You can track your application status using your application number.
            </p>

            <div style={styles.buttonContainer}>
              <button
                onClick={() => onNavigate('home')}
                style={{ ...styles.button, ...styles.buttonSecondary }}
              >
                ← Go to Home
              </button>
              <button
                onClick={() => onNavigate('hostel-login')}
                style={{ ...styles.button, ...styles.buttonPrimary }}
              >
                Go to Login →
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <button
          onClick={() => onNavigate('home')}
          style={styles.backButton}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#f9fafb';
            e.target.style.borderColor = '#6366f1';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'white';
            e.target.style.borderColor = '#e5e7eb';
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          Back to Home
        </button>
      </div>

      {step > 0 && (
        <div style={styles.progressContainer}>
          <div style={styles.progressBar}>
            {[
              { num: 1, label: 'Personal' },
              { num: 2, label: 'Academic' },
              { num: 3, label: 'Guardian' },
              { num: 4, label: 'Additional' }
            ].map((item) => (
              <div key={item.num} style={styles.progressStep}>
                <div style={{
                  ...styles.stepCircle,
                  backgroundColor: step >= item.num ? '#6366f1' : '#e5e7eb',
                  color: step >= item.num ? 'white' : '#9ca3af'
                }}>
                  {step > item.num ? '✓' : item.num}
                </div>
                <span style={{
                  ...styles.stepLabel,
                  color: step >= item.num ? '#6366f1' : '#9ca3af'
                }}>
                  {item.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={styles.formContainer}>
        {error && messageType === 'error' && step > 0 && (
          <div style={styles.error}>❌ {error}</div>
        )}

        {step === 0 && renderPhoneVerification()}
        {step === 1 && renderStep1()}
        {step === 2 && renderStep2()}
        {step === 3 && renderStep3()}
        {step === 4 && renderStep4()}

        {step > 0 && (
          <div style={styles.buttonContainer}>
            {step > 1 && (
              <button
                onClick={handleBack}
                style={{ ...styles.button, ...styles.buttonSecondary }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#e5e7eb'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#f3f4f6'}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M19 12H5M12 19l-7-7 7-7" />
                </svg>
                Previous
              </button>
            )}

            {step < 4 ? (
              <button
                onClick={handleNext}
                style={{ ...styles.button, ...styles.buttonPrimary }}
                onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
                onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
              >
                Next
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={loading}
                style={{
                  ...styles.button,
                  ...styles.buttonPrimary,
                  opacity: loading ? 0.7 : 1,
                  cursor: loading ? 'not-allowed' : 'pointer'
                }}
                onMouseEnter={(e) => !loading && (e.target.style.transform = 'translateY(-2px)')}
                onMouseLeave={(e) => !loading && (e.target.style.transform = 'translateY(0)')}
              >
                {loading ? '⏳ Submitting...' : '✓ Submit Registration'}
                {!loading && (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                    <polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                )}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Styles remain the same
const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#f9fafb',
    padding: '20px',
  },
  header: {
    maxWidth: '900px',
    margin: '0 auto 20px',
  },
  backButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 20px',
    backgroundColor: 'white',
    border: '2px solid #e5e7eb',
    borderRadius: '8px',
    color: '#374151',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  progressContainer: {
    maxWidth: '900px',
    margin: '0 auto 30px',
    backgroundColor: 'white',
    padding: '20px',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  progressBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  progressStep: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    flex: 1,
  },
  stepCircle: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 'bold',
    fontSize: '16px',
    transition: 'all 0.3s',
  },
  stepLabel: {
    marginTop: '8px',
    fontSize: '12px',
    fontWeight: '500',
    transition: 'all 0.3s',
  },
  formContainer: {
    maxWidth: '900px',
    margin: '0 auto',
    backgroundColor: 'white',
    padding: '40px',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  phoneVerificationContainer: {
    textAlign: 'center',
    marginBottom: '30px',
  },
  phoneIcon: {
    fontSize: '48px',
    marginBottom: '20px',
  },
  formTitle: {
    fontSize: '28px',
    fontWeight: 'bold',
    color: '#1f2937',
    marginBottom: '8px',
  },
  formSubtitle: {
    fontSize: '16px',
    color: '#6b7280',
    marginBottom: '32px',
  },
  phoneInputGroup: {
    display: 'flex',
    gap: '12px',
    marginBottom: '20px',
  },
  countryCode: {
    padding: '12px 16px',
    backgroundColor: '#f3f4f6',
    border: '2px solid #e5e7eb',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '500',
    color: '#374151',
  },
  phoneInput: {
    flex: 1,
    padding: '12px 16px',
    fontSize: '16px',
    border: '2px solid #e5e7eb',
    borderRadius: '8px',
    transition: 'all 0.2s',
  },
  otpInput: {
    width: '100%',
    padding: '16px',
    fontSize: '32px',
    fontWeight: 'bold',
    textAlign: 'center',
    letterSpacing: '16px',
    border: '2px solid #e5e7eb',
    borderRadius: '8px',
    marginBottom: '20px',
    transition: 'all 0.2s',
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '20px',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
  },
  formGroupFull: {
    gridColumn: '1 / -1',
  },
  label: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#374151',
    marginBottom: '8px',
  },
  required: {
    color: '#ef4444',
  },
  input: {
    padding: '12px',
    fontSize: '16px',
    border: '2px solid #e5e7eb',
    borderRadius: '8px',
    transition: 'all 0.2s',
  },
  select: {
    padding: '12px',
    fontSize: '16px',
    border: '2px solid #e5e7eb',
    borderRadius: '8px',
    backgroundColor: 'white',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  textarea: {
    padding: '12px',
    fontSize: '16px',
    border: '2px solid #e5e7eb',
    borderRadius: '8px',
    minHeight: '100px',
    resize: 'vertical',
    fontFamily: 'inherit',
    transition: 'all 0.2s',
  },
  passwordHint: {
    fontSize: '12px',
    color: '#6b7280',
    marginTop: '6px',
  },
  passwordStrength: {
    height: '4px',
    borderRadius: '2px',
    marginTop: '8px',
    transition: 'all 0.3s',
  },
  buttonContainer: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    marginTop: '32px',
  },
  button: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 24px',
    fontSize: '16px',
    fontWeight: '500',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  buttonPrimary: {
    backgroundColor: '#6366f1',
    color: 'white',
  },
  buttonSecondary: {
    backgroundColor: '#f3f4f6',
    color: '#374151',
  },
  error: {
    padding: '12px 16px',
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '8px',
    color: '#dc2626',
    marginBottom: '20px',
  },
  infoMessage: {
    backgroundColor: '#eff6ff',
    border: '1px solid #bfdbfe',
    color: '#1e40af',
  },
  warningMessage: {
    backgroundColor: '#fffbeb',
    border: '1px solid #fde68a',
    color: '#92400e',
  },
  successContainer: {
    textAlign: 'center',
    padding: '40px 20px',
  },
  successIcon: {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    backgroundColor: '#d1fae5',
    color: '#10b981',
    fontSize: '48px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 24px',
  },
  successTitle: {
    fontSize: '32px',
    fontWeight: 'bold',
    color: '#1f2937',
    marginBottom: '24px',
  },
  appNumberBox: {
    backgroundColor: '#f0f9ff',
    padding: '24px',
    borderRadius: '12px',
    marginBottom: '24px',
    border: '2px solid #bfdbfe',
  },
  appNumberLabel: {
    fontSize: '14px',
    color: '#1e40af',
    fontWeight: '500',
    marginBottom: '8px',
  },
  appNumberValue: {
    fontSize: '32px',
    fontWeight: 'bold',
    color: '#1e40af',
    letterSpacing: '2px',
  },
  successMessage: {
    fontSize: '16px',
    color: '#6b7280',
    lineHeight: '1.6',
    marginBottom: '32px',
  },
};

export default StudentRegistration;